import { Alert, Button, Checkbox, Col, Form, Modal, Row, Select, Spin } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIdentity } from 'src/core/services/authentication';
import { container } from 'src/inversify.config';
import { NotificationDeleteOptions, NotificationPreferencesStore } from 'src/stores/notifications-store';

export const NotificationsPreferencesDialog: FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const { t } = useTranslation();
  const identity = useIdentity();
  const userId = identity.profile['sub'];

  const [form] = Form.useForm<{
    notificationPreferences: { emailEnabled: boolean; inAppEnabled: boolean; readNotificationRetentionMonths: number };
  }>();
  const [error] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const notificationsPreferencesStore = container.get(NotificationPreferencesStore);
  const notificationsPreferencesState = notificationsPreferencesStore.state;

  useEffect(() => {
    const load = async () => {
      await notificationsPreferencesStore.load(userId);
      setLoading(false);
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = async () => {
    let item = (await form.validateFields()) as {
      notificationPreferences: { emailEnabled: boolean; inAppEnabled: boolean; readNotificationRetentionMonths: number };
    };

    await notificationsPreferencesStore.updatePreferences(userId, item.notificationPreferences);
    onClose();
  };

  return (
    <Modal
      className="figma-modal"
      width={448}
      open={true}
      title={t('NotificationsSettings')}
      onCancel={() => onClose()}
      closable={!loading}
      maskClosable={!loading}
      footer={
        <Row gutter={[24, 0]}>
          <Col span={12}>
            <Button disabled={loading} onClick={() => onClose()} className="big-modal-button primary-outline-button">
              {t('Cancel')}
            </Button>
          </Col>
          <Col span={12}>
            <Button disabled={loading} onClick={onSave} className="big-modal-button" type="primary">
              {t('Save')}
            </Button>
          </Col>
        </Row>
      }
    >
      {(error || '').length !== 0 && <Alert type="error" message={error} />}
      <Spin spinning={loading}>
        {!loading && (
          <Form size="middle" form={form} layout="vertical" autoComplete="off">
            <Form.Item valuePropName="checked" style={{ marginBottom: 12 }}>
              <Form.Item
                style={{ marginBottom: 0 }}
                initialValue={notificationsPreferencesState?.item?.value?.emailEnabled ?? false}
                valuePropName="checked"
                name={['notificationPreferences', 'emailEnabled']}
              >
                <Checkbox>{t('SendNotificationsViaEmail')}</Checkbox>
              </Form.Item>
              <Form.Item
                hidden
                style={{ marginBottom: 0 }}
                initialValue={notificationsPreferencesState?.item?.value?.inAppEnabled ?? true}
                valuePropName="checked"
                name={['notificationPreferences', 'inAppEnabled']}
              >
                <Checkbox>{t('InApp')}</Checkbox>
              </Form.Item>
            </Form.Item>
            <Form.Item
              name={['notificationPreferences', 'readNotificationRetentionMonths']}
              label={t('DeleteAfter')}
              initialValue={notificationsPreferencesState?.item?.value?.readNotificationRetentionMonths ?? NotificationDeleteOptions.ThreeMonths}
            >
              <Select
                options={Object.entries(NotificationDeleteOptions)
                  .filter(([key]) => isNaN(parseInt(key)))
                  .map(([key, value]) => ({ label: t(key), value }))}
              />
            </Form.Item>
          </Form>
        )}
      </Spin>
    </Modal>
  );
};
